import { TELEGRAM_BOT_ID, TELEGRAM_LEGACY_BOT_ID } from '$shared/constants/config';

const OIDC_SCRIPT = 'https://oauth.telegram.org/js/telegram-login.js?3';
const LEGACY_SCRIPT = 'https://telegram.org/js/telegram-widget.js?23';
const DETECT_TIMEOUT = 1500;

export type AuthMethod = 'oidc' | 'widget' | 'link';

export interface OidcResult {
  id_token: string;
  user: {
    sub: string;
    name: string;
    preferred_username: string;
    picture: string;
    phone_number?: string;
  };
}

export interface OidcError {
  error: 'popup_closed' | 'missing id_token' | 'malformed id_token';
}

export type OidcAuthData = OidcResult | OidcError;

export interface WidgetAuthData {
  params: Record<string, any>;
  botId: string;
}

let oidcSupported: boolean | null = null;
let detectPromise: Promise<boolean> | null = null;
const scripts: Record<string, Promise<void>> = {};

let forcedMethod: AuthMethod | null = null;

export function getForcedMethod(): AuthMethod | null {
  if (forcedMethod) return forcedMethod;
  const stored = localStorage.getItem('debug_telegram_auth_method');
  return (stored as AuthMethod) || null;
}

export function setForcedMethod(method: AuthMethod | null): void {
  forcedMethod = method;
  if (method) {
    localStorage.setItem('debug_telegram_auth_method', method);
  } else {
    localStorage.removeItem('debug_telegram_auth_method');
  }
}

function loadScript(src: string): Promise<void> {
  if (src in scripts) return scripts[src];

  scripts[src] = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = src;
    el.async = true;
    el.onload = () => resolve();
    el.onerror = reject;
    document.head.appendChild(el);
  });

  return scripts[src];
}

function hasProxy(): boolean {
  return !!(window as any).TelegramWebviewProxy;
}

function isWebView(): boolean {
  return hasProxy() || !!(window as any).TelegramWebviewProxyProto;
}

function isIOS(): boolean {
  return /ipad|iphone|ipod/i.test(navigator.userAgent);
}

function detect(): Promise<boolean> {
  if (detectPromise) return detectPromise;

  if (!hasProxy()) {
    oidcSupported = false;
    detectPromise = Promise.resolve(false);
    return detectPromise;
  }

  detectPromise = new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => {
      if (oidcSupported === null || oidcSupported === undefined) {
        oidcSupported = false;
      }
      resolve(oidcSupported);
    }, DETECT_TIMEOUT);

    if (!(window as any).Telegram) {
      (window as any).Telegram = {};
    }

    const receiveEvent = (event: string) => {
      if (event === 'oauth_supported') {
        oidcSupported = true;
        clearTimeout(timer);
        resolve(true);
      }
    };

    (window as any).Telegram.WebView = { receiveEvent };
    (window as any).Telegram.TelegramGameProxy = { receiveEvent };

    try {
      (window as any).TelegramWebviewProxy.postEvent('oauth_request', '{}');
    } catch (_e) {
      oidcSupported = false;
      clearTimeout(timer);
      resolve(false);
    }
  });

  return detectPromise;
}

export function init(): void {
  detect().then((ok) => {
    loadScript(ok ? OIDC_SCRIPT : LEGACY_SCRIPT);
  });
}

export async function getMethod(): Promise<AuthMethod> {
  const forced = getForcedMethod();
  if (forced) return forced;

  if (window.isWebApp) return 'link';

  const ok = await detect();
  if (ok) return 'oidc';

  if (isWebView() || isIOS()) return 'link';

  return 'widget';
}

export function getDetectInfo(): {
  hasProxy: boolean;
  isWebView: boolean;
  isIOS: boolean;
  isWebApp: boolean;
  oidcSupported: boolean | null;
  forcedMethod: AuthMethod | null;
} {
  return {
    hasProxy: hasProxy(),
    isWebView: isWebView(),
    isIOS: isIOS(),
    isWebApp: !!window.isWebApp,
    oidcSupported,
    forcedMethod: getForcedMethod(),
  };
}

export async function openOidc(): Promise<OidcAuthData> {
  await loadScript(OIDC_SCRIPT);
  return new Promise<OidcAuthData>((resolve) => {
    (window as any).Telegram.Login.auth(
      { client_id: TELEGRAM_BOT_ID, request_access: ['write'] },
      resolve,
    );
  });
}

export async function openWidget(): Promise<WidgetAuthData> {
  await loadScript(LEGACY_SCRIPT);
  return new Promise<WidgetAuthData>((resolve, reject) => {
    try {
      (window as any).Telegram.Login.auth(
        { bot_id: TELEGRAM_LEGACY_BOT_ID, request_access: 'write' },
        (data: any) =>
          data
            ? resolve({ params: data, botId: TELEGRAM_LEGACY_BOT_ID })
            : reject(new Error('widget returned empty data')),
      );
    } catch (e) {
      reject(e);
    }
  });
}

export function isOidcError(data: OidcAuthData): data is OidcError {
  return 'error' in data;
}
